import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class ErrorBoundary extends React.Component {
  state = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorCode: null
  };

  static getDerivedStateFromError(error) {
    // Generate error code for support reference
    const errorCode = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, errorCode };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  getErrorMessage() {
    const { error } = this.state;
    if (!error) return 'An unexpected error occurred';

    const errorString = error.toString().toLowerCase();

    // Network/API errors
    if (errorString.includes('network') || errorString.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    // Authentication errors
    if (errorString.includes('unauthorized') || errorString.includes('401')) {
      return 'Your session has expired. Please log in again.';
    }

    // Permission errors
    if (errorString.includes('forbidden') || errorString.includes('403')) {
      return 'You do not have permission to perform this action.';
    }

    // Not found errors
    if (errorString.includes('not found') || errorString.includes('404')) {
      return 'The requested resource could not be found.';
    }

    // Database/Server errors
    if (errorString.includes('500') || errorString.includes('server')) {
      return 'A server error occurred. Please try again later.';
    }

    // Component rendering errors
    if (errorString.includes('render') || errorString.includes('component')) {
      return 'A display error occurred. Please refresh the page.';
    }

    // Memory/Resource errors
    if (errorString.includes('memory') || errorString.includes('overflow')) {
      return 'The application ran out of resources. Please restart.';
    }

    return 'An unexpected error occurred in the application.';
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white p-4">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-4">
              <AlertTriangle size={48} className="text-yellow-500" />
            </div>
            <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
            <p className="text-gray-400 mb-4">{this.getErrorMessage()}</p>

            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors mb-4"
            >
              <RefreshCw size={16} />
              Try Again
            </button>

            {this.state.errorCode && (
              <p className="text-xs text-gray-500 mt-4">
                Error Code: <span className="font-mono">{this.state.errorCode}</span>
              </p>
            )}

            <p className="text-xs text-gray-600 mt-2">
              If this problem persists, please contact support with the error code above.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
